/**
 * Admin Service Layer
 *
 * Provides functions to manage admin accounts.
 * Admins bypass all subscription checks and have permanent free access.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './config';

// ============================================================================
// Types
// ============================================================================

export interface FirestoreAdmin {
  email: string;
  addedBy: string;
  addedAt: Timestamp;
}

// ============================================================================
// Admin Service
// ============================================================================

const adminsCollection = collection(db, 'admins');

export const adminsService = {
  /**
   * Check if a user is an admin by their email.
   */
  async isAdmin(email: string): Promise<boolean> {
    if (!email) return false;

    const normalizedEmail = email.toLowerCase().trim();
    const adminRef = doc(db, 'admins', normalizedEmail);
    const adminDoc = await getDoc(adminRef);

    return adminDoc.exists();
  },

  /**
   * Get all admins (only callable by admins).
   */
  async getAll(): Promise<FirestoreAdmin[]> {
    const snapshot = await getDocs(adminsCollection);
    return snapshot.docs.map(doc => ({
      email: doc.id,
      ...doc.data(),
    })) as FirestoreAdmin[];
  },

  /**
   * Add a new admin (only callable by existing admins).
   */
  async add(email: string, addedByEmail: string): Promise<void> {
    const normalizedEmail = email.toLowerCase().trim();

    // Check if already exists
    const adminRef = doc(db, 'admins', normalizedEmail);
    const existing = await getDoc(adminRef);

    if (existing.exists()) {
      throw new Error('This email is already an admin');
    }

    await setDoc(adminRef, {
      email: normalizedEmail,
      addedBy: addedByEmail,
      addedAt: serverTimestamp(),
    });
  },

  /**
   * Remove an admin (only callable by existing admins).
   */
  async remove(email: string): Promise<void> {
    const normalizedEmail = email.toLowerCase().trim();
    const adminRef = doc(db, 'admins', normalizedEmail);

    const existing = await getDoc(adminRef);
    if (!existing.exists()) {
      throw new Error('Admin not found');
    }

    await deleteDoc(adminRef);
  },
};
